interface Props {
  payload: Record<string, unknown>
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const ID_KEY = /(^id|Ids?)$/
// A carrier's own reference number, which staff check before approving whatever it looks like.
const EXTERNAL_ID_KEYS = new Set(['vendorId'])

/**
 * Renders a payload as a two-column key/value table for `create_*` actions.
 * Skips nullish values and our own ids, which mean nothing to the person
 * approving (the card's summary already names what they point at), and renders
 * nothing when no row is left. Unknown shapes (nested objects/arrays) show as JSON.
 */
export function CreatePreview({ payload }: Props) {
  const rows = Object.entries(payload).filter(([k, v]) => v !== null && v !== undefined && !isRawId(k, v))
  if (rows.length === 0) return null
  return (
    // table-fixed + wrap-anywhere: a JSON payload is one unbreakable word, and auto layout sizes the table to it.
    <table className="w-full table-fixed text-sm">
      <tbody>
        {rows.map(([key, value]) => (
          <tr key={key}>
            <td className="text-muted-foreground w-36 py-1 pr-3 align-top font-medium wrap-anywhere">
              {key}
            </td>
            <td className="py-1 wrap-anywhere">{formatValue(value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function isRawId(key: string, value: unknown): boolean {
  if (!ID_KEY.test(key) || EXTERNAL_ID_KEYS.has(key)) return false
  if (typeof value === 'string') return UUID.test(value)
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every((item) => typeof item === 'string' && UUID.test(item))
  )
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return JSON.stringify(value)
}
